import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { bookingService } from '../services/BookingService';
import { configService } from '../services/ConfigService';
import AdminLayout from '../components/AdminLayout';
import '../styles/AdminDashboard.css';

interface Stats {
  totalBookings: number;
  confirmedBookings: number;
  todayBookings: number;
  totalRevenue: number;
  monthlyRevenue: number;
}

function AdminDashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState<Stats>({
    totalBookings: 0,
    confirmedBookings: 0,
    todayBookings: 0,
    totalRevenue: 0,
    monthlyRevenue: 0
  });
  const [totalBooths, setTotalBooths] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      
      // Load booking stats from API
      const bookingStats = await bookingService.getBookingStats();
      setStats(bookingStats);

      // Load booths count from config
      const config = configService.getConfig();
      setTotalBooths(config.booths?.length || 0);
      
      console.log('✅ Dashboard data loaded from database');
    } catch (error) {
      console.error('❌ Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AdminLayout title="لوحة التحكم الرئيسية">
      <motion.div 
        className="dashboard-overview"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        {/* Quick Stats Grid */}
        <div className="stats-grid">
          <motion.div 
            className="stat-card"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.1 }}
          >
            <div className="stat-icon">📅</div>
            <div className="stat-content">
              <div className="stat-number">
                {loading ? '...' : stats.todayBookings}
              </div>
              <div className="stat-label">حجوزات اليوم</div>
            </div>
          </motion.div>

          <motion.div 
            className="stat-card"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2 }}
          >
            <div className="stat-icon">✅</div>
            <div className="stat-content">
              <div className="stat-number">
                {loading ? '...' : stats.confirmedBookings}
              </div>
              <div className="stat-label">الحجوزات المؤكدة</div>
            </div>
          </motion.div>

          <motion.div 
            className="stat-card"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.3 }}
          >
            <div className="stat-icon">🏠</div>
            <div className="stat-content">
              <div className="stat-number">
                {loading ? '...' : totalBooths}
              </div>
              <div className="stat-label">عدد الكابينات</div>
            </div>
          </motion.div>

          <motion.div 
            className="stat-card"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.4 }}
          >
            <div className="stat-icon">💰</div>
            <div className="stat-content">
              <div className="stat-number">
                {loading ? '...' : `${stats.monthlyRevenue}`}
              </div>
              <div className="stat-label">إيرادات الشهر (ر.س)</div>
            </div>
          </motion.div>
        </div>

        {/* Quick Actions */}
        <div className="quick-actions">
          <h2>الإجراءات السريعة</h2>
          <div className="actions-grid">
            <motion.button
              className="action-btn primary"
              onClick={() => navigate('/admin/bookings-management')}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <span className="action-icon">📋</span>
              <span>عرض الحجوزات</span>
            </motion.button>

            <motion.button
              className="action-btn secondary"
              onClick={() => navigate('/admin/pages-management')}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <span className="action-icon">⚙️</span>
              <span>إدارة الإعدادات</span>
            </motion.button>

            <motion.button
              className="action-btn secondary"
              onClick={() => navigate('/admin/promo-codes')}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <span className="action-icon">🎟️</span>
              <span>أكواد الخصم</span>
            </motion.button>

            <motion.button
              className="action-btn secondary"
              onClick={() => navigate('/admin/invoices')}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <span className="action-icon">🧾</span>
              <span>إدارة الفواتير</span>
            </motion.button>
          </div>
        </div>
      </motion.div>
    </AdminLayout>
  );
}

export default AdminDashboard;